import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, renderTitle, requireTitle } from "./types";

function luminance(hex: string): number {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const HEAD_H = 280;
const BAR_H = 76;
const STOPS_PAD_Y = 8;
const STOP_PAD_Y = 8;
const CONNECTOR_H = 56;
const PIN_PAD_X = 60;

// 2-stop pins have ~520px of vertical room per stop vs ~323px for 3-stop, so
// the image can be larger without crowding the text column. 3-stop keeps the
// historical sizing — squeezing the text any harder makes long place names
// wrap badly. `stopAlign` + `imgMarginTop` together place the image such that
// its top sits flush with the .stop-name's top (the .stop-label eyebrow lives
// above in the space the imgMarginTop reserves).
function imageLayoutForStops(numStops: number): {
  imgW: number;
  imgH: number;
  stopAlign: "center" | "flex-start";
  imgMarginTop: number;
} {
  if (numStops === 2) {
    return { imgW: 460, imgH: 460, stopAlign: "flex-start", imgMarginTop: 32 };
  }
  return { imgW: 320, imgH: 280, stopAlign: "center", imgMarginTop: 0 };
}

function buildTrailPaths(
  numStops: number,
  imgW: number,
  imgH: number,
  stopAlign: "center" | "flex-start",
  imgMarginTop: number,
): string {
  if (numStops < 2) return "";
  const xLeft = PIN_PAD_X + imgW / 2;
  const xRight = PIN_WIDTH - PIN_PAD_X - imgW / 2;

  const stopsTopY = HEAD_H + STOPS_PAD_Y;
  const stopsContentH = PIN_HEIGHT - HEAD_H - BAR_H - 2 * STOPS_PAD_Y;
  const stopH = (stopsContentH - (numStops - 1) * CONNECTOR_H) / numStops;
  const stopContentH = stopH - 2 * STOP_PAD_Y;
  const imgYOffset =
    stopAlign === "flex-start"
      ? imgMarginTop
      : (stopContentH - imgH) / 2;

  const paths: string[] = [];
  for (let i = 0; i < numStops - 1; i++) {
    const stopTop_i = stopsTopY + i * (stopH + CONNECTOR_H);
    const imgBottom_i = stopTop_i + STOP_PAD_Y + imgYOffset + imgH;
    const stopTop_next = stopTop_i + stopH + CONNECTOR_H;
    const imgTop_next = stopTop_next + STOP_PAD_Y + imgYOffset;
    const gap = imgTop_next - imgBottom_i;

    const xStart = i % 2 === 0 ? xRight : xLeft;
    const xEnd = (i + 1) % 2 === 0 ? xRight : xLeft;
    const c1y = imgBottom_i + gap * 0.55;
    const c2y = imgTop_next - gap * 0.55;

    const yA = Math.round(imgBottom_i);
    const yB = Math.round(imgTop_next);
    paths.push(
      `<path d="M${xStart} ${yA} C ${xStart} ${Math.round(c1y)}, ${xEnd} ${Math.round(c2y)}, ${xEnd} ${yB}" />`,
    );
  }
  return paths.join("");
}

export function carouselTemplate(input: PinInput): string {
  const { eyebrow, titleAccent, brand, brandLogo, cta, palette, typography, stops } = input;
  const title = requireTitle(input, "carousel");
  if (!stops || stops.length < 2) {
    throw new Error("carousel template requires at least 2 stops");
  }
  // Length→size ladder (same pattern as the venue templates) so long titles
  // shrink instead of silently truncating at the 2-line clamp.
  const titleLen = title.length;
  const titleSize = titleLen <= 32 ? 74 : titleLen <= 44 ? 66 : titleLen <= 58 ? 56 : 48;

  // Bottom bar uses palette.ink as background. For dark palettes ink is light,
  // so the logo must be dark for contrast.
  const isDarkPalette = luminance(palette.bg) < luminance(palette.ink);
  const brandLogoFilter = isDarkPalette
    ? "brightness(0)"
    : "brightness(0) invert(1)";

  const rows = stops
    .map((s, i) => {
      const reverse = i % 2 === 1;
      const num = i + 1;
      const stopHtml = `
      <div class="stop ${reverse ? "rev" : ""}">
        <div class="stop-text">
          <div class="stop-label">${escapeHtml(s.label ?? `Stop ${num}`)}</div>
          <div class="stop-name"><span class="num">${num}</span>${escapeHtml(s.name)}</div>
          ${s.caption ? `<div class="stop-cap">${escapeHtml(s.caption)}</div>` : ""}
        </div>
        <div class="stop-img" style="background-image:url('${escapeHtml(s.imageUrl)}')"></div>
      </div>`;
      const isLast = i === stops.length - 1;
      const connector = isLast ? "" : `<div class="connector"></div>`;
      return stopHtml + connector;
    })
    .join("");

  const { imgW, imgH, stopAlign, imgMarginTop } = imageLayoutForStops(stops.length);
  const trailPaths = buildTrailPaths(stops.length, imgW, imgH, stopAlign, imgMarginTop);
  const trailSvg = trailPaths
    ? `<svg class="trail" viewBox="0 0 ${PIN_WIDTH} ${PIN_HEIGHT}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">${trailPaths}</svg>`
    : "";

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;background:${palette.bg};color:${palette.ink};font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased}
  .pin{position:relative;width:100%;height:100%;display:flex;flex-direction:column}
  .trail{position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:0}
  .trail path{fill:none;stroke:${palette.accent};stroke-width:3;stroke-dasharray:9 7;stroke-linecap:round;opacity:.95}
  .head{flex:0 0 ${HEAD_H}px;padding:56px 40px 28px;border-bottom:2px dashed ${palette.soft};position:relative;z-index:1}
  .eyebrow{font-size:24px;letter-spacing:7px;text-transform:uppercase;color:${palette.primary};font-weight:700;margin-bottom:14px}
  h1{font-family:${typography.headlineFont};font-size:${titleSize}px;font-weight:${typography.headlineWeight};line-height:1.18;letter-spacing:-1.5px;color:${palette.ink};display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;padding-bottom:.08em}
  h1 .accent{font-style:italic;font-weight:400;color:${palette.accentText ?? palette.ink}}
  .stops{flex:1;display:flex;flex-direction:column;padding:${STOPS_PAD_Y}px 60px;position:relative;z-index:1}
  .stop{flex:1;min-height:0;display:flex;align-items:${stopAlign};gap:32px;padding:${STOP_PAD_Y}px 0}
  .stop.rev{flex-direction:row-reverse}
  .connector{flex:0 0 ${CONNECTOR_H}px}
  .stop-text{flex:1;min-width:0}
  .stop-label{font-size:22px;letter-spacing:5px;text-transform:uppercase;color:${palette.primary};font-weight:700;margin-bottom:10px}
  .stop-name{font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};font-size:46px;line-height:1.18;color:${palette.ink};display:flex;align-items:center;gap:18px;margin-bottom:8px;padding-bottom:.08em}
  .stop-name .num{flex:0 0 64px;width:64px;height:64px;border-radius:50%;background:${palette.carouselStopNumBg ?? palette.accent};color:${palette.numText ?? palette.ink};font-size:32px;display:inline-flex;align-items:center;justify-content:center;letter-spacing:-1px;line-height:1}
  .stop-cap{font-size:34px;line-height:1.35;color:${palette.ink};opacity:.85;padding-left:82px}
  /* Reversed stops swap image/text positions only — keep number on the left
     and caption left-aligned for consistent reading order across all stops. */
  .stop-img{flex:0 0 ${imgW}px;width:${imgW}px;height:${imgH}px;margin-top:${imgMarginTop}px;background-size:cover;background-position:center;border-radius:20px;box-shadow:0 18px 36px rgba(0,0,0,.18);position:relative;z-index:2}
  /* CTA sits on the LEFT, brand logo on the RIGHT. Pinterest's Lens
     overlay (visual-search magnifying glass) parks in the bottom-right
     corner of the image area on some pins and would otherwise occlude
     the CTA — moving brand to that corner trades brand visibility
     (still readable through Lens) for CTA clickability.
     CTA color uses palette.bg (the same as brand text on the right) so
     it always reads cleanly against the bar's palette.ink background,
     across every palette. Letter-spacing tightened because the longer
     CTA wording ("Visit site to get the full plan") needs the room. */
  .bar{flex:0 0 ${BAR_H}px;background:${palette.ink};color:${palette.bg};display:flex;align-items:center;justify-content:space-between;padding:0 60px;font-size:20px;letter-spacing:2px;text-transform:uppercase;font-weight:700;position:relative;z-index:1}
  .bar .brand-logo{height:40px;width:auto;display:block;filter:${brandLogoFilter}}
  .bar .cta{color:${palette.bg};letter-spacing:1.5px}
</style></head>
<body><div class="pin">
  ${trailSvg}
  <div class="head">
    ${eyebrow ? `<div class="eyebrow">${escapeHtml(eyebrow)}</div>` : ""}
    <h1>${renderTitle(title, titleAccent)}</h1>
  </div>
  <div class="stops">${rows}</div>
  <div class="bar"><span class="cta">${escapeHtml(cta ?? "Visit site to get the full plan")}</span>${
    input.hideLogo
      ? ""
      : brandLogo
        ? `<img class="brand-logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
        : `<span>${escapeHtml(brand)}</span>`
  }</div>
</div></body></html>`;
}
